import { ThemeProvider } from './contexts/ThemeContext';
import BottomNav from './components/BottomNav';
import { useState, useEffect, useMemo } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Home from './pages/Home';
import Profile from './pages/Profile';
import Messages from './pages/Messages';
import Notifications from './pages/Notifications';
import Trials from './pages/Trials';
import TrialDetail from './pages/TrialDetail';
import AskAI from './pages/AskAI';
import AIDiscover from './pages/AIDiscover';
import Dashboard from './pages/Dashboard';
import Jobs from './pages/Jobs';
import JobDetail from './pages/JobDetail';
import Discover from './pages/Discover';
import Connections from './pages/Connections';
import Settings from './pages/Settings';
import EditProfile from './pages/EditProfile';
import Search from './pages/Search';
import Explore from './pages/Explore';
import ParentalConsent from './pages/ParentalConsent';

const AUTH_ROUTES = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/parental-consent'];

function ConditionalBottomNav({ isAuthenticated }) {
  const location = useLocation();
  if (!isAuthenticated || AUTH_ROUTES.includes(location.pathname)) return null;
  return <BottomNav />;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Initialize from localStorage synchronously
    return !!localStorage.getItem('token');
  });
  const [loading, setLoading] = useState(false);

  // Listen for logout events to immediately clear auth state
  useEffect(() => {
    const handleLogout = () => setIsAuthenticated(false);
    window.addEventListener('auth-logout', handleLogout);
    return () => window.removeEventListener('auth-logout', handleLogout);
  }, []);

  // Protected route wrapper
  const protectedElement = (element) => {
    if (loading) {
      return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
    }
    return isAuthenticated ? element : <Navigate to="/login" />;
  };

  // Public route wrapper (redirect to home if authenticated)
  const publicElement = (element) => {
    if (loading) {
      return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
    }
    return !isAuthenticated ? element : <Navigate to="/home" />;
  };

  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={publicElement(<Login setIsAuthenticated={setIsAuthenticated} />)} />
            <Route path="/register" element={<Register setIsAuthenticated={setIsAuthenticated} />} />
            <Route path="/forgot-password" element={publicElement(<ForgotPassword />)} />
            <Route path="/reset-password" element={publicElement(<ResetPassword />)} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/parental-consent" element={<ParentalConsent />} />
            <Route path="/home" element={protectedElement(<Home />)} />
            <Route path="/explore" element={protectedElement(<Explore />)} />
            <Route path="/dashboard" element={protectedElement(<Dashboard />)} />
            <Route path="/profile/:username" element={protectedElement(<Profile />)} />
            <Route path="/messages" element={protectedElement(<Messages />)} />
            <Route path="/notifications" element={protectedElement(<Notifications />)} />
            <Route path="/trials" element={protectedElement(<Trials />)} />
            <Route path="/trials/:trialId" element={protectedElement(<TrialDetail />)} />
            <Route path="/jobs" element={protectedElement(<Jobs />)} />
            <Route path="/jobs/:jobId" element={protectedElement(<JobDetail />)} />
            <Route path="/discover" element={protectedElement(<Discover />)} />
            <Route path="/connections" element={protectedElement(<Connections />)} />
            <Route path="/settings" element={protectedElement(<Settings />)} />
            <Route path="/edit-profile" element={protectedElement(<EditProfile />)} />
            <Route path="/ask-ai" element={protectedElement(<AskAI />)} />
            <Route path="/ai-discover" element={protectedElement(<AIDiscover />)} />
            <Route path="/search" element={protectedElement(<Search />)} />
            <Route path="/" element={<Navigate to="/home" />} />
          </Routes>
          <ConditionalBottomNav isAuthenticated={isAuthenticated} />
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
